/**
 * @fileoverview Capa de Lógica de Negocio (Business Logic - BL) para la dinámica 'Memorama Individual' (Tile ID 51).
 * Provee normalización de datos, protección de privacidad (LFPDPPP), gestión de sesión de juego,
 * telemetría dual no bloqueante (SQL Server + Google Tag Manager / GA4) y validación de reglas de participación.
 * 
 * @module utilities/BL/MemoryIndividual
 */

import { ValidateSqlInjection } from './Security';
import {
    GetPublicConfig as DLGetPublicConfig,
    RegisterParticipant as DLRegisterParticipant,
    StartGameSession as DLStartGameSession,
    SubmitResult as DLSubmitResult,
    GetLeaderboard as DLGetLeaderboard,
    TrackMetric as DLTrackMetric,
    GetAdminThemes as DLGetAdminThemes,
    GetAdminThemeById as DLGetAdminThemeById,
    SaveAdminTheme as DLSaveAdminTheme,
    DeleteAdminTheme as DLDeleteAdminTheme,
    SelectActiveTheme as DLSelectActiveTheme,
    UploadCardImage as DLUploadCardImage,
    SendQROverWebSocket as DLSendQROverWebSocket,
    GetAdminStats as DLGetAdminStats,
    GetAdminParticipants as DLGetAdminParticipants,
    DisqualifyParticipant as DLDisqualifyParticipant,
    UndisqualifyParticipant as DLUndisqualifyParticipant,
    SetParticipantWinner as DLSetParticipantWinner,
    ToggleAdminDynamicStatus as DLToggleAdminDynamicStatus,
    ResetAdminDynamic as DLResetAdminDynamic,
    GetExportExcelUrl as DLGetExportExcelUrl
} from '../DL/MemoryIndividual';

// =============================================================================
// CONSTANTES DE ALMACENAMIENTO
// =============================================================================

const SESSION_STORAGE_KEY = 'psl_memory_session_id';
const PARTICIPANT_STORAGE_KEY = 'psl_memory_participant_data';

// =============================================================================
// ACCESO SEGURO A ALMACENAMIENTO (SSR / MODO INCÓGNITO)
// =============================================================================

const getLocalStorage = () => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
        if (typeof localStorage !== 'undefined') return localStorage;
    } catch (e) {
        // Modo incógnito o restricciones de seguridad en navegador
    }
    return null;
};

const getSessionStorage = () => {
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) return window.sessionStorage;
        if (typeof sessionStorage !== 'undefined') return sessionStorage;
    } catch (e) {
        // Modo incógnito o restricciones de seguridad en navegador
    }
    return null;
};

// =============================================================================
// GESTIÓN DE SESIÓN DE JUEGO (SESSION MANAGEMENT)
// =============================================================================

/**
 * Obtiene el identificador único anónimo de sesión o genera uno nuevo con timestamp y Math.random.
 * 
 * @function GetSessionId
 * @returns {string} Identificador único de sesión (ej: 'sess_abc1234_1728392100000').
 */
export const GetSessionId = () => {
    try {
        const sessStorage = getSessionStorage();
        if (sessStorage) {
            let sessionId = sessStorage.getItem(SESSION_STORAGE_KEY);
            if (!sessionId) {
                sessionId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
                sessStorage.setItem(SESSION_STORAGE_KEY, sessionId);
            }
            return sessionId;
        }
    } catch (e) {
        console.warn("Advertencia en GetSessionId (sessionStorage inaccesible):", e);
    }
    return 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
};

/**
 * Guarda los datos de registro y token OTT de sesión del participante de forma persistente.
 * 
 * @function SaveStoredParticipant
 * @param {Object} data - Datos del participante { participantId, sessionToken, name, email, phone, ... }
 * @returns {boolean} True si se guardó correctamente.
 */
export const SaveStoredParticipant = (data) => {
    try {
        const sessStorage = getSessionStorage();
        if (sessStorage && data) {
            sessStorage.setItem(PARTICIPANT_STORAGE_KEY, JSON.stringify(data));
            return true;
        }
        const locStorage = getLocalStorage();
        if (locStorage && data) {
            locStorage.setItem(PARTICIPANT_STORAGE_KEY, JSON.stringify(data));
            return true;
        }
    } catch (e) {
        console.warn("No se pudo guardar la sesión del participante:", e);
    }
    return false;
};

/**
 * Obtiene los datos del participante almacenados en la sesión actual.
 * 
 * @function GetStoredParticipant
 * @returns {Object|null} Datos del participante almacenados o null si no existen.
 */
export const GetStoredParticipant = () => {
    try {
        const sessStorage = getSessionStorage();
        if (sessStorage) {
            const raw = sessStorage.getItem(PARTICIPANT_STORAGE_KEY);
            if (raw) return JSON.parse(raw);
        }
        const locStorage = getLocalStorage();
        if (locStorage) {
            const raw = locStorage.getItem(PARTICIPANT_STORAGE_KEY);
            if (raw) return JSON.parse(raw);
        }
    } catch (e) {
        console.warn("Error al recuperar datos del participante:", e);
    }
    return null;
};

/**
 * Elimina los datos del participante almacenados en la sesión.
 * 
 * @function ClearStoredParticipant
 */
export const ClearStoredParticipant = () => {
    try {
        const sessStorage = getSessionStorage();
        if (sessStorage) {
            sessStorage.removeItem(PARTICIPANT_STORAGE_KEY);
        }
        const locStorage = getLocalStorage();
        if (locStorage) {
            locStorage.removeItem(PARTICIPANT_STORAGE_KEY);
        }
    } catch (e) {
        console.warn("Error al limpiar datos del participante:", e);
    }
};

// =============================================================================
// NORMALIZACIÓN, PRIVACIDAD (LFPDPPP) Y FORMATEO
// =============================================================================

/**
 * Normaliza y extrae estrictamente los 10 dígitos numéricos de un teléfono.
 * Limpia prefijos comunes como +52, 52, 521 y símbolos.
 * 
 * @function NormalizePhone
 * @param {string|number} phone - Teléfono ingresado por el aficionado.
 * @returns {string} Teléfono a 10 dígitos numéricos o cadena vacía.
 */
export const NormalizePhone = (phone) => {
    if (!phone) return '';
    let digits = String(phone).replace(/\D/g, '');

    // Formato México celular antiguo con 521 (13 dígitos: 521 + 10 dígitos)
    if (digits.length === 13 && digits.startsWith('521')) {
        return digits.slice(3);
    }

    // Formato México con código de país 52 (12 dígitos numéricos: 52 + 10 dígitos)
    if (digits.length === 12 && digits.startsWith('52')) {
        return digits.slice(2);
    }

    // Cualquier prefijo internacional con código 52 que exceda 10 dígitos
    if (digits.length > 10 && digits.startsWith('52')) {
        return digits.slice(-10);
    }

    // Si tiene más de 10 dígitos pero no coincide con los patrones anteriores, tomar los últimos 10
    if (digits.length > 10) {
        return digits.slice(-10);
    }

    return digits;
};

/**
 * Normaliza una dirección de correo electrónico eliminando espacios y convirtiendo a minúsculas.
 * 
 * @function NormalizeEmail
 * @param {string} email - Correo ingresado por el aficionado.
 * @returns {string} Correo normalizado.
 */
export const NormalizeEmail = (email) => {
    if (!email) return '';
    return String(email).trim().toLowerCase();
};

/**
 * Enmascara el nombre completo para visualización sutil conforme a la LFPDPPP (ej: 'Juan Pérez' -> 'J*** P****').
 * 
 * @function MaskName
 * @param {string} name - Nombre completo del usuario.
 * @returns {string} Nombre enmascarado.
 */
export const MaskName = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name
        .trim()
        .split(/\s+/)
        .map(word => {
            if (word.length <= 1) return word;
            return word[0] + '*'.repeat(Math.max(1, word.length - 1));
        })
        .join(' ');
};

/**
 * Enmascara un número telefónico mostrando únicamente los últimos 4 dígitos (ej: '4441234567' -> '******4567').
 * 
 * @function MaskPhone
 * @param {string} phone - Teléfono a enmascarar.
 * @returns {string} Teléfono enmascarado.
 */
export const MaskPhone = (phone) => {
    if (!phone) return '';
    const clean = String(phone).replace(/\D/g, '');
    if (clean.length < 4) return clean;
    return `******${clean.slice(-4)}`;
};

/**
 * Enmascara una dirección de correo electrónico conforme a la LFPDPPP (ej: 'juan@adsl.mx' -> 'j***@adsl.mx').
 * 
 * @function MaskEmail
 * @param {string} email - Correo electrónico a enmascarar.
 * @returns {string} Correo enmascarado.
 */
export const MaskEmail = (email) => {
    if (!email || typeof email !== 'string' || !email.includes('@')) return email || '';
    const [user, domain] = email.trim().toLowerCase().split('@');
    if (!user) return `***@${domain || ''}`;
    return `${user[0]}***@${domain}`;
};

/**
 * Convierte un intervalo en milisegundos a formato estándar mm:ss.ms (ej: 23456 ms -> '00:23.456').
 * 
 * @function FormatTime
 * @param {number} ms - Tiempo transcurrido en milisegundos.
 * @returns {string} Tiempo formateado mm:ss.ms.
 */
export const FormatTime = (ms) => {
    const numMs = Number(ms);
    if (isNaN(numMs) || numMs < 0) return '00:00.000';
    const minutes = Math.floor(numMs / 60000);
    const seconds = Math.floor((numMs % 60000) / 1000);
    const remainingMs = numMs % 1000;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(remainingMs).padStart(3, '0')}`;
};

// =============================================================================
// EXTRACCIÓN DE TRÁFICO URL
// =============================================================================

/**
 * Extrae y normaliza el origen de tráfico desde los parámetros de la URL (?src=, ?s=, ?utm_source= o ?source=).
 * Mapea 'qr', 'qr_screen', 'screen_qr' y 'pantalla' a 'qr_screen' y establece 'direct' como valor por defecto.
 * 
 * @function ExtractTrafficSource
 * @param {string} [searchString] - Cadena de búsqueda opcional (window.location.search).
 * @returns {string} Origen de tráfico normalizado ('direct', 'qr_screen', etc.).
 */
export const ExtractTrafficSource = (searchString) => {
    try {
        let search = searchString;
        if (search === undefined && typeof window !== 'undefined' && window.location) {
            search = window.location.search;
            if (!search && window.location.hash && window.location.hash.includes('?')) {
                search = window.location.hash;
            }
            if (!search && window.location.href && window.location.href.includes('?')) {
                search = window.location.href;
            }
        }
        if (!search || typeof search !== 'string') {
            return 'direct';
        }
        if (search.includes('?')) {
            search = search.substring(search.indexOf('?'));
        }
        const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
        const rawSource = params.get('src') || params.get('s') || params.get('utm_source') || params.get('source') || '';
        const trimmed = rawSource.trim().toLowerCase();

        if (!trimmed) {
            return 'direct';
        }
        if (trimmed === 'qr' || trimmed === 'qr_screen' || trimmed === 'screen_qr' || trimmed === 'pantalla') {
            return 'qr_screen';
        }
        return trimmed;
    } catch (e) {
        console.warn("Error al extraer traffic source:", e);
        return 'direct';
    }
};

// =============================================================================
// DESPACHADOR DE TELEMETRÍA DUAL NO BLOQUEANTE (SQL + GTM/GA4)
// =============================================================================

/**
 * Despacha un evento de telemetría al embudo dual (Google Tag Manager y SQL Server) de forma no bloqueante.
 * Cumple con estricta política de CERO PII (ningún dato personal es transmitido en dataLayer).
 * 
 * @async
 * @function TrackFunnelMetric
 * @param {string} eventName - Nombre del evento ('page_view', 'form_complete', 'game_start', 'game_complete').
 * @param {string|null} [source=null] - Origen del tráfico ('direct', 'qr_screen', etc.).
 * @param {Object} [extraData={}] - Parámetros métricos complementarios anonimizados ({ timeMs, attempts, themeId }).
 * @returns {Promise<{success: boolean}>}
 */
export const TrackFunnelMetric = async (eventName, source = null, extraData = {}) => {
    const sessionId = GetSessionId();
    const resolvedSource = source || 'direct';

    // 1. Despacho a Google Tag Manager / dataLayer y GA4 con CERO PII
    try {
        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: `psl_memory_${eventName}`,
                sessionId,
                source: resolvedSource,
                timestamp: new Date().toISOString(),
                ...(extraData.timeMs !== undefined ? { timeMs: extraData.timeMs } : {}),
                ...(extraData.attempts !== undefined ? { attempts: extraData.attempts } : {}),
                ...(extraData.themeId !== undefined ? { themeId: extraData.themeId } : {})
            });

            if (typeof window.gtag === 'function') {
                window.gtag('event', `psl_memory_${eventName}`, {
                    session_id: sessionId,
                    event_category: 'memory_individual_dynamic',
                    event_label: eventName,
                    traffic_source: resolvedSource
                });
            }
        }
    } catch (e) {
        console.warn("Fallo no bloqueante al registrar telemetría en dataLayer / GA4:", e);
    }

    // 2. Despacho a SQL Server vía DL
    try {
        await DLTrackMetric(eventName, sessionId, resolvedSource);
    } catch (err) {
        console.warn("Fallo no bloqueante al invocar DLTrackMetric:", err);
    }

    return { success: true };
};

// =============================================================================
// MÉTODOS DE DOMINIO Y CASOS DE USO
// =============================================================================

/**
 * Obtiene la configuración pública activa para el aficionado.
 * 
 * @async
 * @function GetPublicConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetPublicConfig = async () => {
    return await DLGetPublicConfig();
};

/**
 * Valida, normaliza y registra la participación del aficionado, emite token OTT y despacha telemetría.
 * 
 * @async
 * @function RegisterParticipant
 * @param {Object} formData - { name, email, phone, abonado, isAbonado, acceptedTerms, source }
 * @returns {Promise<{success: boolean, code?: string, message?: string, participantId?: number, sessionToken?: string, previousTime?: string, formattedTime?: string}>}
 */
export const RegisterParticipant = async ({ name, email, phone, abonado, isAbonado, acceptedTerms, source }) => {
    // 1. Validaciones preventivas de presencia y formato
    const rawName = name ? String(name).trim() : '';
    if (!rawName || rawName.length < 2) {
        return { success: false, message: 'Por favor ingresa tu nombre completo.' };
    }

    const normalizedEmail = NormalizeEmail(email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
        return { success: false, message: 'Por favor ingresa un correo electrónico válido.' };
    }

    const normalizedPhone = NormalizePhone(phone);
    if (!normalizedPhone || normalizedPhone.length !== 10) {
        return { success: false, message: 'Por favor ingresa un número de teléfono celular válido a 10 dígitos.' };
    }

    if (!acceptedTerms) {
        return { success: false, message: 'Debes aceptar los términos y condiciones para participar.' };
    }

    // 2. Sanitización contra vectores de inyección SQL
    try {
        const isNameDangerous = await ValidateSqlInjection(rawName);
        const isEmailDangerous = await ValidateSqlInjection(normalizedEmail);
        const isPhoneDangerous = await ValidateSqlInjection(normalizedPhone);
        if (isNameDangerous || isEmailDangerous || isPhoneDangerous) {
            return { success: false, message: 'Se detectaron caracteres no permitidos en el formulario.' };
        }
    } catch {
        // En caso de error en el validador, se permite continuar si los regex son válidos
    }

    const resolvedSource = source || ExtractTrafficSource();
    const resolvedIsAbonado = Boolean(
        isAbonado === true ||
        (abonado && (String(abonado).toLowerCase() === 'sí' || String(abonado).toLowerCase() === 'si'))
    );
    const resolvedAbonado = resolvedIsAbonado ? 'Sí' : 'No';

    // 3. Envío al Data Layer
    const result = await DLRegisterParticipant({
        name: rawName,
        email: normalizedEmail,
        phone: normalizedPhone,
        abonado: resolvedAbonado,
        isAbonado: resolvedIsAbonado,
        acceptedTerms: Boolean(acceptedTerms),
        source: resolvedSource
    });

    // 4. Si fue exitoso o reanudó sesión previa, almacenar datos y despachar telemetría
    if (result && result.success && result.participantId) {
        SaveStoredParticipant({
            participantId: result.participantId,
            sessionToken: result.sessionToken,
            name: rawName,
            email: normalizedEmail,
            phone: normalizedPhone,
            abonado: resolvedAbonado,
            isAbonado: resolvedIsAbonado,
            previousTime: result.previousTime,
            formattedTime: result.formattedTime,
            source: resolvedSource,
            savedAt: new Date().toISOString()
        });

        // Despachar telemetría de formulario completado
        TrackFunnelMetric('form_complete', resolvedSource);
    }

    return result;
};

/**
 * Inicia la partida registrando la marca de tiempo oficial del servidor y despachando telemetría.
 * 
 * @async
 * @function StartGameSession
 * @param {number} participantId - Identificador del participante.
 * @param {string} sessionToken - Token OTT de sesión emitido en el registro.
 * @param {string|null} [source=null] - Origen del tráfico.
 * @returns {Promise<{success: boolean, message?: string, serverStartTimeUtc?: string}>}
 */
export const StartGameSession = async (participantId, sessionToken, source = null) => {
    const resolvedSource = source || ExtractTrafficSource();
    const result = await DLStartGameSession({ participantId, sessionToken });

    if (result && result.success) {
        TrackFunnelMetric('game_start', resolvedSource);
    }

    return result;
};

/**
 * Envía el resultado final del aficionado al motor anti-cheat del backend y registra la métrica de compleción.
 * 
 * @async
 * @function SubmitResult
 * @param {Object} params - { participantId, sessionToken, clientTimeMs, formattedTime, attempts, themeId, source }
 * @returns {Promise<{success: boolean, status?: string, message?: string, timeElapsedMs?: number, formattedTime?: string}>}
 */
export const SubmitResult = async ({
    participantId,
    sessionToken,
    clientTimeMs,
    formattedTime,
    attempts,
    themeId,
    source
}) => {
    const resolvedSource = source || ExtractTrafficSource();
    const calculatedFormattedTime = formattedTime || FormatTime(clientTimeMs);

    const payload = {
        participantId,
        sessionToken,
        clientTimeMs: Number(clientTimeMs) || 0,
        formattedTime: calculatedFormattedTime,
        attempts: Number(attempts) || 0
    };

    const result = await DLSubmitResult(payload);

    // Despacho no bloqueante de telemetría de juego completado con métricas numéricas anonimizadas
    TrackFunnelMetric('game_complete', resolvedSource, {
        timeMs: payload.clientTimeMs,
        attempts: payload.attempts,
        themeId: themeId || null
    });

    return result;
};

/**
 * Obtiene la tabla oficial de clasificación (leaderboard).
 * 
 * @async
 * @function GetLeaderboard
 * @param {number} [top=50] - Cantidad máxima de posiciones a consultar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const GetLeaderboard = async (top = 50) => {
    return await DLGetLeaderboard(top);
};

// =============================================================================
// RE-EXPORTACIÓN Y LÓGICA DE OPERACIONES ADMINISTRATIVAS (TILE 51)
// =============================================================================

/**
 * Resuelve una ruta relativa o absoluta de imagen al host base del backend o mantiene rutas estáticas locales.
 * Sanitiza esquemas no seguros y maneja assets de frontend (public/img) vs uploads del backend (/uploads).
 * 
 * @function resolveMediaUrl
 * @param {string} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL normalizada lista para renderizado en <img>.
 */
export const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath || typeof mediaPath !== 'string') return '';
    const cleanPath = mediaPath.trim();
    if (!cleanPath) return '';

    // Bloqueo estricto de esquemas peligrosos
    if (/^(javascript|vbscript|data:(?!image\/))/i.test(cleanPath)) {
        return '';
    }

    // Protocolos absolutos, base64 data URIs o blob URLs
    if (
        cleanPath.startsWith('http://') ||
        cleanPath.startsWith('https://') ||
        cleanPath.startsWith('data:image/') ||
        cleanPath.startsWith('blob:') ||
        cleanPath.startsWith('//')
    ) {
        return cleanPath;
    }

    // Recursos estáticos del frontend (carpeta public/img/...)
    if (cleanPath.startsWith('/img/') || cleanPath.startsWith('img/')) {
        return cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    }

    // Recursos subidos al backend (/uploads/...)
    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:7205/api/';
    let backendOrigin = apiBase.replace(/\/api\/?$/i, '').replace(/\/+$/, '');

    // Ajuste dinámico de host para pruebas en red local / Wi-Fi (IP del servidor)
    if (typeof window !== 'undefined' && window.location && window.location.hostname &&
        window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        if (backendOrigin.includes('localhost') || backendOrigin.includes('127.0.0.1')) {
            backendOrigin = backendOrigin.replace('localhost', window.location.hostname).replace('127.0.0.1', window.location.hostname);
        }
        if (window.location.protocol === 'http:' && backendOrigin.startsWith('https://')) {
            backendOrigin = backendOrigin.replace('https://', 'http://');
        }
    }

    const sanitizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    return `${backendOrigin}${sanitizedPath}`;
};

/**
 * Formatea una fecha UTC al estándar de fecha y hora de México ('es-MX').
 * 
 * @function formatMexicoDate
 * @param {string|Date} dateStr - Cadena de fecha ISO UTC o timestamp.
 * @returns {string} Fecha formateada en español México (ej: '09/10/2026, 14:30:15 hrs').
 */
export const formatMexicoDate = (dateStr) => {
    if (!dateStr) return 'No disponible';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);
    return date.toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }) + ' hrs';
};

/**
 * Consulta las estadísticas consolidadas del embudo de conversión y padrón.
 * 
 * @async
 * @function GetAdminStats
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetAdminStats = async () => {
    return await DLGetAdminStats();
};

/**
 * Consulta el padrón de participantes con soporte de búsqueda, estatus y paginación.
 * 
 * @async
 * @function GetAdminParticipants
 * @param {Object} [filterParams={}] - { search, status, page, pageSize }
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetAdminParticipants = async (filterParams = {}) => {
    return await DLGetAdminParticipants(filterParams);
};

/**
 * Descalifica a un aficionado con motivo justificado para auditoría administrativa.
 * 
 * @async
 * @function DisqualifyParticipant
 * @param {number|string} id - ID del participante.
 * @param {string} reason - Motivo fundado de la descalificación (mínimo 3 caracteres).
 * @param {string} [adminUser='ADMIN'] - Nombre del operador o administrador que autoriza.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const DisqualifyParticipant = async (id, reason, adminUser = 'ADMIN') => {
    if (!id) {
        return { success: false, message: 'Identificador del participante no válido.' };
    }
    const trimmed = (reason || '').trim();
    if (!trimmed || trimmed.length < 3) {
        return { success: false, message: 'El motivo de descalificación debe tener al menos 3 caracteres.' };
    }
    return await DLDisqualifyParticipant(id, { reason: trimmed, adminUser });
};

/**
 * Revierte la descalificación de un aficionado.
 * 
 * @async
 * @function UndisqualifyParticipant
 * @param {number|string} id - ID del participante.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const UndisqualifyParticipant = async (id) => {
    if (!id) {
        return { success: false, message: 'Identificador del participante no válido.' };
    }
    return await DLUndisqualifyParticipant(id);
};

/**
 * Establece o revoca el estatus de ganador de un aficionado.
 * 
 * @async
 * @function SetParticipantWinner
 * @param {number|string} id - ID del participante.
 * @param {boolean} isWinner - Indica si el aficionado es ganador oficial.
 * @param {number} [rank=0] - Posición en el podio de ganadores.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const SetParticipantWinner = async (id, isWinner, rank = 0) => {
    if (!id) {
        return { success: false, message: 'Identificador del participante no válido.' };
    }
    return await DLSetParticipantWinner(id, { isWinner: Boolean(isWinner), winnerRank: Number(rank) || 0 });
};

/**
 * Activa o pausa la dinámica de Memorama Individual.
 * 
 * @async
 * @function ToggleAdminDynamicStatus
 * @param {boolean} isActive - Estado deseado para la dinámica.
 * @returns {Promise<{success: boolean, isActive?: boolean, message?: string}>}
 */
export const ToggleAdminDynamicStatus = async (isActive) => {
    const payload = typeof isActive === 'object' && isActive !== null
        ? { isActive: Boolean(isActive.isActive) }
        : { isActive: Boolean(isActive) };
    return await DLToggleAdminDynamicStatus(payload);
};

/**
 * Reinicia la dinámica de Memorama Individual (limpia registros previos al partido).
 * 
 * @async
 * @function ResetAdminDynamic
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const ResetAdminDynamic = async () => {
    return await DLResetAdminDynamic();
};

/**
 * Retorna la URL para exportar el padrón y métricas a Excel.
 * 
 * @function GetExportExcelUrl
 * @returns {string} URL de descarga directa.
 */
export const GetExportExcelUrl = () => {
    return DLGetExportExcelUrl();
};

export const GetAdminThemes = DLGetAdminThemes;
export const GetAdminThemeById = DLGetAdminThemeById;
export const SaveAdminTheme = DLSaveAdminTheme;
export const DeleteAdminTheme = DLDeleteAdminTheme;
export const SelectActiveTheme = DLSelectActiveTheme;
export const UploadCardImage = DLUploadCardImage;
export const SendQROverWebSocket = DLSendQROverWebSocket;

const BLMemoryIndividual = {
    GetSessionId,
    SaveStoredParticipant,
    GetStoredParticipant,
    ClearStoredParticipant,
    NormalizePhone,
    NormalizeEmail,
    MaskName,
    MaskPhone,
    MaskEmail,
    FormatTime,
    ExtractTrafficSource,
    TrackFunnelMetric,
    GetPublicConfig,
    RegisterParticipant,
    StartGameSession,
    SubmitResult,
    GetLeaderboard,
    GetAdminThemes,
    GetAdminThemeById,
    SaveAdminTheme,
    DeleteAdminTheme,
    SelectActiveTheme,
    UploadCardImage,
    SendQROverWebSocket,
    formatMexicoDate,
    GetAdminStats,
    GetAdminParticipants,
    DisqualifyParticipant,
    UndisqualifyParticipant,
    SetParticipantWinner,
    ToggleAdminDynamicStatus,
    ResetAdminDynamic,
    GetExportExcelUrl,
    resolveMediaUrl
};

export default BLMemoryIndividual;
